/**
 * 빈 상태, 미준비 상태, 오류 상태의 안내 경계.
 *
 * Owns:
 * - 안내 문장 조판과 크기 3단계. 글자·버튼·여백이 함께 바뀐다.
 * - 동작 버튼 하나의 모양과 배치.
 * - tone 에 따른 글자 색, 기본 아이콘, 보조기술 알림(role).
 * - `lg` 가 받은 높이를 채우고 가로·세로 가운데에 놓이는 배치.
 * - `fill` 자리의 배경 확장과 안내 내용의 가운데 배치.
 * - 카드마다 반복되는 짧은 사유를 표시하는 주황 칩 클래스(`noticeChipClassName`).
 * - 내용과 높이를 맞춘 자리의 틀 클래스(`stateFrameClassName`).
 *
 * Does not own:
 * - 안내 문구, 버튼 문구와 동작. 소비처가 전달한다.
 * - 로딩 표시. 대기 상태는 `LoadingState`(`@/shared/ui/loadingState`)가 표시한다.
 */
import {
  IconButton,
  InfoIcon,
  WarningIcon,
  type IconButtonSize,
} from "@carbon6/c6-frontend-design";
import type { ReactNode } from "react";
import styles from "./EmptyState.module.css";

export type EmptyStateSize = "sm" | "md" | "lg";

export type EmptyStateAction = {
  label: string;
  onClick: () => void;
  // 버튼 앞 아이콘. 이 계층은 앱 아이콘 모음을 import 할 수 없으므로 소비처가 전달한다.
  icon?: ReactNode;
  // 새로고침·다시 시도 같은 보조 동작은 surface 다. 빈 상태를 벗어나는 주 동작(추가)은 solid 다.
  variant?: "surface" | "solid";
};

export type EmptyStateProps = {
  // 무엇이 일어났고 다음에 무엇을 하면 되는지 알리는 완결된 문장.
  children: ReactNode;
  // sm 은 패널 안 한 줄, md 는 패널 자리, lg 는 페이지 본문.
  size?: EmptyStateSize;
  // 부모가 나눠 준 공간을 배경 자체가 채운다. 높이가 없는 부모에서는 내용 높이를 쓴다.
  fill?: boolean;
  tone?: "neutral" | "error";
  // primary 는 sunken 면을 깐다. plain 은 이미 면 위에 있는 자리이므로 면을 깔지 않는다.
  // surface 는 sunken 바탕 위의 자리다. 같은 sunken 면은 보이지 않으므로 한 단계 위 면(surface)을 깐다.
  mode?: "primary" | "plain" | "surface";
  // sm 의 줄 정렬. 틀의 가운데에 두는 자리는 center 다. 폭이 모자라서 버튼이 다음 줄로 내려가도
  // 버튼이 가운데에 선다. md·lg 는 항상 가운데다.
  align?: "start" | "center";
  // 문장 앞 장식 아이콘. 뜻은 문장이 전하므로 보조기술에는 숨긴다. 생략하면 tone 의 기본 아이콘을
  // 그린다(neutral 은 info, error 는 warning). `null` 은 아이콘 없음이다.
  icon?: ReactNode | null;
  action?: EmptyStateAction;
};

// 글자 크기가 커지면 버튼도 한 단계씩 커진다. 문장과 버튼의 비율을 자리마다 다시 고르지 않는다.
const ACTION_SIZE: Record<EmptyStateSize, IconButtonSize> = { sm: "xs", md: "sm", lg: "md" };

// 카드마다 반복되는 짧은 사유와 머리 줄 안의 상태를 표시하는 주황 칩. 이 자리에 EmptyState 를 두면
// 같은 아이콘과 면이 카드마다 반복된다. 칩은 글자 폭만 차지한다. 배치는 소비처가 소유한다.
export const noticeChipClassName = styles.noticeChip;

// 내용과 높이를 맞춘 자리(차트, 영상, 로그 창, 코드 에디터)의 틀. sunken 면의 가운데에 안내를 둔다. 틀 안의
// 안내는 `mode="plain"` 이다. 높이는 소비처가 정한다. 그래서 내용이 오면 같은 자리에 그려진다.
export const stateFrameClassName = styles.stateFrame;

export function EmptyState({
  children,
  size = "md",
  fill = false,
  tone = "neutral",
  mode = "primary",
  align = "start",
  icon,
  action,
}: EmptyStateProps): ReactNode {
  const message = <p className={styles.message}>{children}</p>;
  const glyph = icon === undefined ? tone === "error" ? <WarningIcon /> : <InfoIcon /> : icon;
  const state = (
    <div
      className={styles.root}
      data-size={size}
      data-fill={fill || undefined}
      data-tone={tone}
      data-mode={mode}
      data-align={align}
      role={tone === "error" ? "alert" : "status"}
    >
      {glyph === null ? (
        message
      ) : (
        // 아이콘과 문장은 한 덩어리다. 폭이 모자라도 아이콘만 윗줄에 남지 않는다.
        <div className={styles.labeled}>
          <span className={styles.icon} aria-hidden="true">
            {glyph}
          </span>
          {message}
        </div>
      )}
      {action === undefined ? null : (
        <IconButton
          text={action.label}
          variant={action.variant ?? "surface"}
          size={ACTION_SIZE[size]}
          onClick={action.onClick}
        >
          {action.icon}
        </IconButton>
      )}
    </div>
  );
  return size === "lg" && !fill ? <div className={styles.fill}>{state}</div> : state;
}
